import React, { useState } from "react";
import Card from "components/card";
import {
  MdHistory,
  MdInsertDriveFile,
  MdPerson,
  MdCalendarToday,
  MdSearch,
} from "react-icons/md";

// ─── Sample hardcoded records ─────────────────────────────────────────────────
const SAMPLE_RECORDS = [
  {
    id: 1,
    filename: "attendance_week30_2026.xlsx",
    uploadedBy: "Admin",
    uploadedAt: "2026-07-28 08:14",
    totalStaff: 42,
    notified: 38,
    failed: 4,
    status: "Completed",
  },
  {
    id: 2,
    filename: "attendance_week29_2026.xlsx",
    uploadedBy: "Admin",
    uploadedAt: "2026-07-21 07:52",
    totalStaff: 40,
    notified: 40,
    failed: 0,
    status: "Completed",
  },
  {
    id: 3,
    filename: "attendance_week28_2026.xlsx",
    uploadedBy: "Admin",
    uploadedAt: "2026-07-14 09:03",
    totalStaff: 38,
    notified: 35,
    failed: 3,
    status: "Completed",
  },
  {
    id: 4,
    filename: "attendance_week27_2026.xlsx",
    uploadedBy: "Admin",
    uploadedAt: "2026-07-07 08:30",
    totalStaff: 41,
    notified: 0,
    failed: 41,
    status: "Failed",
  },
  {
    id: 5,
    filename: "attendance_week31_2026.xlsx",
    uploadedBy: "Admin",
    uploadedAt: "2026-08-03 07:45",
    totalStaff: 44,
    notified: 0,
    failed: 0,
    status: "Processing",
  },
];

const statusConfig = {
  Completed: { cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  Failed:    { cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  Processing:{ cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
};

// ─────────────────────────────────────────────────────────────────────────────

const ShiftRecords = () => {
  const [search, setSearch] = useState("");

  const filtered = SAMPLE_RECORDS.filter((r) =>
    r.filename.toLowerCase().includes(search.toLowerCase())
  );

  const totalUploads = SAMPLE_RECORDS.length;

  return (
    <div className="mt-5 h-full">
      {/* ── Page header ── */}
      <div className="mb-6 flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <MdHistory className="h-8 w-8 text-brand-500" />
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Shift Records</h2>
          <span className="rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white">
            {totalUploads} uploads
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          History of attendance data uploads and SMS dispatch results
        </p>
      </div>

      {/* ── Records table ── */}
      <Card extra="p-0 overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-4 dark:border-navy-700 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-base font-semibold text-navy-700 dark:text-white">Upload History</h3>
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-navy-600 dark:bg-navy-800">
            <MdSearch className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search filename..."
              className="w-48 bg-transparent text-sm text-gray-600 outline-none placeholder-gray-400 dark:text-white"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-navy-700">
            <thead className="bg-gray-50 dark:bg-navy-800">
              <tr>
                {["#", "File", "Uploaded By", "Date & Time", "Total Staff"].map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 ${i === 0 ? "w-10 text-left" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-navy-700 dark:bg-navy-700">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400 dark:text-gray-500">
                    No records match your search.
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr key={r.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-navy-600">
                    <td className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MdInsertDriveFile className="h-5 w-5 flex-shrink-0 text-brand-400" />
                        <span className="text-sm font-medium text-navy-700 dark:text-white">{r.filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                        <MdPerson className="h-4 w-4 text-gray-400" />
                        {r.uploadedBy}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                        <MdCalendarToday className="h-4 w-4 text-gray-400" />
                        {r.uploadedAt}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-navy-700 dark:text-white">
                      {r.totalStaff}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ShiftRecords;

